import { createFileRoute, redirect } from "@tanstack/react-router";

import { getPendingInvites } from "~/stores/onboarding";
import { activeOrganization$ } from "~/stores/organizations";

/**
 * Resume entry point for the onboarding wizard.
 *
 * Two columns drive resume:
 *   - `users.onboarding` (`user | invitations | workspace | done`) tracks
 *     personal progress through profile + workspace-decision.
 *   - `organizations.onboarding` (`company | datasource | catalog | keyTables
 *     | invite | contact | boards | sample | plan | done`) tracks the active org's setup. Looked up from
 *     `activeOrganization$` once user-level onboarding is done.
 *
 * User-level resume always wins -- the user must finish profile/workspace
 * before any org-level step matters. Once user-level is `"done"`, we read
 * the active org's column (or fall through to `/` if there is no active
 * org -- `_app/index` then handles the create-vs-app triage).
 */
export const Route = createFileRoute("/onboard/")({
  beforeLoad: ({ context }) => {
    const userOnboarding = context.user?.onboarding;
    // A user who arrived via an invite link (`/join` or `/join/board`)
    // is funneled to the unified invitations chooser when they are on
    // the personal `workspace` branch.
    const pendingInvites = getPendingInvites();
    const hasPendingInvite =
      !!pendingInvites.orgInviteId || !!pendingInvites.boardInviteId;
    switch (userOnboarding) {
      case "user":
        throw redirect({ to: "/onboard/user", replace: true });
      case "verifyEmail":
        throw redirect({ to: "/onboard/verify-email", replace: true });
      case "invitations":
        throw redirect({ to: "/onboard/invitations", replace: true });
      case "workspace":
        if (hasPendingInvite) {
          throw redirect({ to: "/onboard/invitations", replace: true });
        }
        throw redirect({ to: "/onboard/workspace", replace: true });
      case "done":
        break;
      default:
        throw redirect({ to: "/onboard/user", replace: true });
    }

    const org = activeOrganization$.get();
    if (!org?.id) {
      throw redirect({ to: "/", replace: true });
    }
    switch (org.onboarding) {
      case "company":
      case "datasource":
        throw redirect({ to: "/onboard/datasource", replace: true });
      case "catalog":
        throw redirect({ to: "/onboard/catalog", replace: true });
      case "keyTables":
        throw redirect({ to: "/onboard/key-tables", replace: true });
      case "invite":
        throw redirect({ to: "/onboard/invite", replace: true });
      case "contact":
        throw redirect({ to: "/onboard/contact", replace: true });
      case "boards":
        throw redirect({ to: "/onboard/boards", replace: true });
      case "sample":
        throw redirect({ to: "/onboard/sample", replace: true });
      case "plan":
        // Legacy/transitional marker — the trial/plan step was removed, so
        // resume straight to the celebration screen.
        throw redirect({ to: "/onboard/done", replace: true });
      default:
        throw redirect({ to: "/", replace: true });
    }
  },
});
